import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { JhiLanguageService, EventManager, AlertService } from 'ng-jhipster';
import { Reservation } from './reservation.model';
import { ReservationService } from './reservation.service';
import { ReservationUtilService } from './reservation.util.service';
import { PaymentItem, PaymentItemService} from '../payment-item/';
import { CRUD_ACTIONS, BackOfficeService } from '../../shared';
import { Subscription } from 'rxjs/Subscription';
import * as moment from 'moment-timezone';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

@Component({
    selector: 'jhi-reservation-detail',
    templateUrl: './reservation-detail.component.html'
})
export class ReservationDetailComponent implements OnInit, OnDestroy {

    reservation: Reservation;
    private subscription: any;
    paymentItems: PaymentItem[];
    paymentItemsTotalAmount = 0;
    readonly MAX_PAYMENT_ITEMS = 50;
    private paymentItemSubscription: any;
    private reservationSubscription: Subscription;
    private paymentItemsCount: number;

    // variables to handle checkout session
    public comesFromSuccessCheckoutSession: boolean;
    public comesFromCancelCheckoutSession: boolean;
    public checkoutURLStatus: string;
    private readonly CHECKOUT_SUCCESS = 'success';
    private readonly CHECKOUT_CANCEL = 'cancel';
    private retryCount = 0;
    private MAX_RETRY = 3;
    private MAX_TIME_DIFF = 15; // threshold in seconds;

    constructor(
        private jhiLanguageService: JhiLanguageService,
        private reservationService: ReservationService,
        private route: ActivatedRoute,
        private reservationUtilService: ReservationUtilService,
        private paymentItemService: PaymentItemService,
        private eventManager: EventManager,
        private alertService: AlertService,
        private backofficeService: BackOfficeService
    ) {
        this.jhiLanguageService.setLocations(['reservation', 'reservationStatus', 'classType', 'card', 'lesson',
        'payment', 'paymentType', 'paymentItem']);
    }

    ngOnInit() {
        this.comesFromSuccessCheckoutSession = false;
        this.comesFromCancelCheckoutSession = false;
        this.subscription = this.route.params.subscribe(params => {
            this.load(params['id']);
            this.checkoutURLStatus = this.route.snapshot.queryParams['checkout']
            ?  this.route.snapshot.queryParams['checkout']
            : null;
        });
        this.paymentItemSubscription = this.eventManager
            .subscribe('paymentItemListModification', (response) => {
                this.loadPaymentItems(this.reservation.id);
            });
        this.reservationSubscription = this.eventManager
            .subscribe('reservationListModification',
            (response) => {
                this.reservation = response['content']['content'];
            });
    }

    private isLoadingPayments() {
        return this.paymentItems === null && this.paymentItemsCount !== 0 ;
    }

    createCheckoutSession() {
        if (!this.reservation) {
            console.log('Error creating the checkout session: There is no reservation');
            return;
        }

        this.backofficeService
            .createCheckoutSession(this.reservation.school, this.reservation.id)
            .subscribe( (res: string) => {
                this.onReservationCheckoutCreated(res);
            } , (error: Response) => console.log('Error creating the checkout session: Unexpected error from the server'));
    }

    private onReservationCheckoutCreated(chechoutUrl: string) {
        // we open the checkout session in same browser so the user can pay
         window.location.href = chechoutUrl;
    }

    private syncPaymentInformation() {
        this.paymentItemsTotalAmount = 0;
        this.reservation.isPayed = false;

        if (this.isFreeOrBelongsToCard()) {
            this.reservation.isPayed = true;
            return;
        }

        if (this.paymentItems) {
            this.paymentItems.forEach((paymentItem) => {
                this.paymentItemsTotalAmount += paymentItem.amount;
            });
        }

        if (this.paymentItemsTotalAmount >= this.reservation.lesson.classType.standardUnitPrice) {
            this.reservation.isPayed = true;
        }
    }

    private isFree(): boolean {
        return this.reservation && this.reservation.lesson.free === true;
    }

    getReservationBadgeStyle(): String {
        if (this.reservation) {
            return this.reservationUtilService.getReservationBadgeStyle(
                this.reservation.status.toString());
        }
        return 'badge-info';
    }

    private isFreeOrBelongsToCard() {
        return this.reservation &&
        (this.reservation.lesson.free || this.belongsToCard());
    }

    private belongsToCard() {
        return this.reservation && this.reservationUtilService.belongsToProduct(this.reservation);
    }

    private getFormule(reservation: Reservation) {
        return this.reservationUtilService.getPaymentMethod(reservation);
    }

    private isOnlinePaymentsEnabled() {
        return this.backofficeService.isOnlinePaymentsEnabled(this.reservation.school) &&
        this.reservation.student.acceptOnlinePayments;
    }

    load(id) {
        this.reservationService.find(id).subscribe(reservation => {
            this.reservation = reservation;
            this.loadPaymentItems(reservation.id);
        });
    }

    private loadPaymentItems(id: number) {
        this.paymentItemsCount = 0;
        this.paymentItems = null;
        this.paymentItemsTotalAmount = 0;

        this.paymentItemService.count({
            query: 'reservation.id:' + id
        })
        .subscribe(
            (count: number) => {
                this.paymentItemsCount = count;

                if (this.paymentItemsCount <= 0) {
                    this.syncPaymentInformation();
                    this.handleCheckoutCancelMessage();
                    return;
                }

                if (this.paymentItemsCount > 0 ) {
                    this.paymentItemService.search({
                        query: 'reservation.id:' + id,
                        size: this.MAX_PAYMENT_ITEMS,
                        sort: ['payment.id', 'desc']
                    }).subscribe(
                        (res: HttpResponse<PaymentItem[]>) => this.onPaymentItemSuccess(res.body, res.headers),
                        (res: HttpErrorResponse) => this.onError(res)
                    );
                }
            },
            (res: HttpErrorResponse) => {
                this.onError(res);
            }
        );
    }

    private onPaymentItemSuccess(data, headers) {
        this.paymentItems = data;
        this.syncPaymentInformation();
        this.handleCheckoutSuccessMessage();
    }

    private handleCheckoutCancelMessage() {
        if (this.reservation.backofficeSessionId === null) {
            return;

        }

        if (this.checkoutURLStatus === this.CHECKOUT_CANCEL) {
            this.comesFromCancelCheckoutSession = true;
        }
    }

    private handleCheckoutSuccessMessage() {
        if (this.reservation.backofficeSessionId === null || this.checkoutURLStatus === this.CHECKOUT_CANCEL) {
            return;
        }

        if (this.paymentItems && this.paymentItems.length > 0) {
            // show the payment success message if the payment has been created before the threashhold
            // and the reservation has a checkout id
            const now = new moment.utc();
            let createdDate: moment;
            this.paymentItems.forEach(paymentItem => {
                createdDate = new moment.tz(paymentItem.createdDate, 'utc');
                if (this.checkoutURLStatus === this.CHECKOUT_SUCCESS && now.diff(createdDate, 'seconds') < this.MAX_TIME_DIFF ) {
                    this.comesFromSuccessCheckoutSession = true;
                }
            });

            return;
        }

        // at this point there is a probability that there is an ongoing checkout session, or a session
        // has been finished, the creation of the paymentitem can take some time
        if (this.retryCount < this.MAX_RETRY) {
            this.retryCount++;
            console.log('retrying to get payment items, retry count: ' + this.retryCount);
            setTimeout(() => {
                this.loadPaymentItems(this.reservation.id);
                }, 500);
        }
    }

    previousState() {
        window.history.back();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.paymentItemSubscription.unsubscribe();
        this.reservationSubscription.unsubscribe();
    }

    private onError(error: HttpErrorResponse) {
        this.alertService.error(error.error, null, null);
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/app/entities/reservation/reservation-detail.component.ts